<script>
import FormGroup from '@maison/components/form/FormGroup.vue';
import PhoenixForm from '@maison/components/form/PhoenixForm.vue';
import TextField from '@maison/components/form/fields/TextField.vue';
import PasswordField from '@maison/components/form/fields/PasswordField';
import LoginFormWithGemini from '@phoenix/mixins/components/form/LoginFormWithGemini';
import SendPhoneTokenForm from '@maison/components/myAccount/SendPhoneTokenForm';
import PhoneTokenField from '@maison/components/form/fields/PhoneTokenField';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { useHydraCustomerAuth } from '@module/Hydra/resources/assets/js/composables/hydraCustomerAuth';

export default {
  components: {
    FormGroup,
    PhoenixForm,
    TextField,
    PasswordField,
    PhoneTokenField,
    SendPhoneTokenForm,
    PhxsgButton,
  },

  mixins: [LoginFormWithGemini],

  props: {
    shouldDisplayTitle: {
      type: Boolean,
      default: true,
    },
    shouldDisplayFilledCheckmark: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['formLoaded'],

  setup() {
    return {
      hydraAuth: useHydraCustomerAuth(),
    };
  },

  computed: {
    retrievePasswordLinkClass() {
      return 'text-xs';
    },

    customPiplStatement() {
      return this.$t('Form.pipl.loginStatement');
    },

    getPasswordHelper() {
      return '';
    },
  },

  methods: {
    getRetrievePasswordLinkClass() {
      return 'text-xs';
    },
  },
};
</script>

<template>
  <div>
    <send-phone-token-form
      v-if="showTokenOrPasswordButtons"
      ref="sendPhoneTokenForm"
      :mobile-phone="fields.emailOrPhone.value"
      :show-errors-in-toast="shouldShowErrorInToast"
      :custom-pipl-statement="customPiplStatement"
      type="login"
      @on-success="sendTokenSuccess"
      @on-fail="sendTokenFail"
    >
      <fieldset class="fieldset fieldset--vertical">
        <div class="fieldset__fields grid gap-base gap-md">
          <form-group :attrs="fields.emailOrPhone" data-cy="login-form-email">
            <text-field v-model="fields.emailOrPhone.value" :should-validate="false" />
            <template v-if="fields.emailOrPhone.disabled" #help>
              <div class="field-helper-text">
                <PhxsgButton variant="link" @click.prevent.stop="back">
                  {{ $t('MyAccount.goBack') }}
                </PhxsgButton>
              </div>
            </template>
          </form-group>
        </div>
      </fieldset>

      <template #form-actions>
        <PhxsgButton
          ref="sendTokenPhoneButton"
          variant="plain"
          color="primary"
          full
          data-cy="use-token-btn"
          @click.prevent="sendToken"
        >
          {{ $t('MyAccount.sendPhoneToken') }}
        </PhxsgButton>

        <p class="conjunction text-muted">
          {{ $t('Form.or') }}
        </p>

        <PhxsgButton
          variant="plain"
          color="secondary"
          full
          data-cy="use-password-btn"
          @click.prevent.stop="usePassword"
        >
          {{ $t('MyAccount.usePassword') }}
        </PhxsgButton>
      </template>
    </send-phone-token-form>

    <phoenix-form
      v-else
      ref="phoenixForm"
      v-model="fields"
      :action="formAction"
      :prefill-enabled="true"
      :redirect-url="redirectUrl"
      :refresh-ping="true"
      :show-errors-in-toast="shouldShowErrorInToast"
      :should-display-required-text="false"
      :should-display-submit-button="showLoginButton"
      :custom-submit-text="$t('MyAccount.login')"
      :on-success-callback="onSubmitSuccess"
      :should-display-chinese-pipl-statement="true"
      :custom-pipl-statement="customPiplStatement"
      submit-button-full
      :is-submit-disabled="hasPhoneTokenTooManyRetries && showTokenField"
      :should-display-filled-checkmark="shouldDisplayFilledCheckmark"
      :before-submit="beforeSubmit"
      data-cy="main-login-form"
      @form-loaded="onFormLoaded"
    >
      <fieldset class="fieldset fieldset--vertical">
        <legend v-if="shouldDisplayTitle" class="fieldset__legend">
          <div class="headings headings--centered">
            <h2 class="h4">{{ $t('Form.loginTitle') }}</h2>
          </div>
        </legend>

        <div class="fieldset__fields grid gap-base gap-md">
          <form-group :attrs="fields.emailOrPhone" data-cy="login-form-email">
            <text-field v-model="fields.emailOrPhone.value" />
            <template v-if="fields.emailOrPhone.disabled" #help>
              <div class="field-helper-text">
                <PhxsgButton variant="link" @click.prevent.stop="back">
                  {{ $t('MyAccount.goBack') }}
                </PhxsgButton>
              </div>
            </template>
          </form-group>

          <template v-if="showPasswordField">
            <form-group :attrs="fields.password" data-cy="login-form-password">
              <password-field v-model="fields.password.value" />
            </form-group>
          </template>
        </div>

        <PhxsgButton
          v-if="showPasswordField"
          use="a"
          :href="getRetrievePasswordUrl()"
          variant="link"
          class="text-sm mt-2xs"
        >
          {{ $t('MyAccount.forgotYourPassword') }}
        </PhxsgButton>

        <div v-if="showTokenField">
          <p v-if="hasPhoneTokenTooManyRetries" class="field-helper-text--error">
            {{ $t('Authentication.phone.token.retryIn', { timeLeft: timeLeftBeforeTokenRetry }) }}
          </p>
          <div v-else>
            <form-group :attrs="fields.phoneToken" data-cy="login-form-token">
              <phone-token-field v-model="fields.phoneToken.value" @resend-phone-token="backToPhoneStep" />
            </form-group>
          </div>
        </div>
      </fieldset>

      <template #form-actions="slotProps">
        <div v-if="showNextButton" style="width: 100%">
          <PhxsgButton
            :disabled="isNextButtonDisabled || slotProps.isDisabled"
            variant="plain"
            color="primary"
            full
            data-cy="next-btn"
            @click.prevent.stop="next"
          >
            {{ $t('MyAccount.loginNext') }}
          </PhxsgButton>
        </div>
      </template>
    </phoenix-form>
  </div>
</template>
